import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import {
    ILoginReminder,
    ILoginReminderLog,
    ILoginReminderSentOverview,
} from '@fi-app/other-settings/fi-login-reminder/fi-login-reminder';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
} from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiLoginReminderService } from '@fi-app/other-settings/fi-login-reminder/fi-login-reminder.service';
import { FiLoginReminderSentResultType } from '@fi-app/other-settings/fi-login-reminder/fi-login-reminder-enums';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiDateTimeFormatOptions, FiDateTimeSecFormatOptions } from '@fi-shared/utils';

@Component({
    selector: 'fi-login-reminder-log',
    templateUrl: './fi-login-reminder-log.component.html',
    styleUrl: './fi-login-reminder-log.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-login-reminder-overview' },
    standalone: false,
})
export class FiLoginReminderLogComponent extends FiRemoteStateFullDataSource<ILoginReminderLog> implements OnInit {
    @Input({ required: true }) loginReminder: ILoginReminder;
    @Input({ required: true }) sentOverview: ILoginReminderSentOverview;

    defaultSortColumnName = 'CreatedOn';
    defaultSortDirection = true;
    objectType: ObjectTypes;
    itemsListType = FiItemsListType.LoginReminderLog;

    constructor(
        injector: Injector,
        private fiLoginReminderService: FiLoginReminderService,
        private fiSnackBarService: FiSnackBarService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(injector);
    }

    ngOnInit(): void {}

    loadListDataFn(loadArgs?: ILoadDataArgs): Promise<IItemsListDataFetchResult<ILoginReminderLog>> {
        const parameter = this.getParameter(loadArgs);
        return this.fiLoginReminderService.getLoginReminderLogsListData(parameter);
    }

    private getParameter(loadArgs: ILoadDataArgs): IItemsListParameterBase {
        const parameter = super.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            //FetchTotalCount: !loadArgs.fetchOnlyItems,
            //FetchLightWeightObjects: !loadArgs.fetchOnlyItems, // todo should we implement local search ?
        };
        return parameter;
    }

    protected readonly actions: Readonly<IItemsListAction2<ILoginReminderLog>[]> = [
        {
            title: this.appTextService.get('login.send_reminder'),
            onClick: (item) => {
                this.fiLoginReminderService.sendLoginReminder(item.UserId).then(() => {
                    this.fiSnackBarService.showSnackbar({
                        textMessage: this.appTextService.get('login.reminder_was_sent', {
                            '{user_name}': item.UserName,
                        }),
                        buttonText: this.appTextService.get('common.dismiss'),
                        iconClass: 'fa-solid fa-circle-info',
                    });
                });
            },
            iconClass: 'fa-regular fa-phone',
            optionType: FiItemsListOptionType.Icon,
        },
    ];

    protected getEmailText(item: ILoginReminderLog): string {
        //item.EmailSentResult = <any>FiLoginReminderSentResultType.OptionIsNotSelected; for tests

        if (item.EmailSentResult === FiLoginReminderSentResultType.Ok) return item.Email;

        if (item.EmailSentResult === FiLoginReminderSentResultType.ErrorNotFound)
            return this.appTextService.get('login.email_not_found');

        if (item.EmailSentResult === FiLoginReminderSentResultType.ErrorOther)
            return this.appTextService.get('login.send_email_failed');

        /*if (item.EmailSentResult === FiLoginReminderSentResultType.OptionWasNotSelected)
         */

        if (item.EmailSentResult === FiLoginReminderSentResultType.OptionIsNotSelected) {
            if (!this.sentOverview.EmailWasSent) {
                return this.appTextService.get('login.email_was_not_selected');
            }
            return this.appTextService.get('login.email_is_not_selected');
        }
    }

    protected getPhoneNumberText(item: ILoginReminderLog): string {
        //item.SMSSentResult = <any>FiLoginReminderSentResultType.OptionIsNotSelected; for tests

        if (item.SMSSentResult === FiLoginReminderSentResultType.Ok) return item.PhoneNumber;

        if (item.SMSSentResult === FiLoginReminderSentResultType.ErrorNotFound)
            return this.appTextService.get('login.phone_number_not_found');

        if (item.SMSSentResult === FiLoginReminderSentResultType.ErrorOther)
            return this.appTextService.get('login.send_sms_failed');

        if (item.SMSSentResult === FiLoginReminderSentResultType.OptionIsNotSelected) {
            if (!this.sentOverview.SmsWasSent) {
                return this.appTextService.get('login.sms_was_not_selected');
            }

            return this.appTextService.get('login.sms_is_not_selected');
        }
    }

    protected getReminderSentDateText(item: ILoginReminderLog): string {
        // todo
        if (this.isLoginReminderSendingFailed(item)) {
            return '-';
        }
        return this.fiFormatDateService.formatDate(item.CreatedOn, FiDateTimeSecFormatOptions, {
            showToday: false,
        });
    }

    protected getLoggedInAfterReminderText(item: ILoginReminderLog): string {
        // todo change to yes/no
        return this.fiFormatDateService.formatDate(item.LastLoginTime, FiDateTimeSecFormatOptions, {
            showToday: false,
        });
    }

    private isLoginReminderSendingFailed(item: ILoginReminderLog) {
        return (
            item.EmailSentResult !== FiLoginReminderSentResultType.Ok &&
            item.SMSSentResult !== FiLoginReminderSentResultType.Ok
        );
    }

    protected showEmailColumn() {
        return this.loginReminder.ViaEmail || this.sentOverview.EmailWasSent;
    }

    protected showSmsColumn() {
        return this.loginReminder.ViaSMS || this.sentOverview.SmsWasSent;
    }
}
